import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // Importa useTranslation
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { User, Building, ArrowRight, ChevronRight, Globe, Eye, EyeOff } from 'lucide-react';
import { resolveAssetPath } from '../config/basePath';
import GymLogo from '../components/layout/GymLogo';
import GymAccessRequestForm, { emptyGymRequestForm } from '../components/auth/GymAccessRequestForm';
import { DASHBOARD_BG_DARK, DASHBOARD_BG_LIGHT } from '../constants/dashboardBackgrounds';

const LoginPage = () => {
  const { t } = useTranslation(); // Aggiungi useTranslation per le traduzioni
  const { isDark } = useTheme();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [loginErrorCode, setLoginErrorCode] = useState('');
  const [showGymInfo, setShowGymInfo] = useState(false);
  const [gymInfo, setGymInfo] = useState(emptyGymRequestForm);
  const [requestStatus, setRequestStatus] = useState({ message: '', type: '' });
  const [showPassword, setShowPassword] = useState(false);

  const { login, currentUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || '/';

  // WEBAPP-031: Mostra messaggio da verify 403 (piano inattivo / Elite) dopo redirect a login
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem('login_message');
      const code = sessionStorage.getItem('login_message_code');
      if (stored) {
        setLoginError(stored);
        if (code) {
          setLoginErrorCode(code);
        }
        sessionStorage.removeItem('login_message');
        sessionStorage.removeItem('login_message_code');
      }
    } catch (e) {}
  }, []);

  useEffect(() => {
    if (currentUser) {
      if (currentUser.role_name === 'admin') {
        navigate('/admin', { replace: true });
      } else if (currentUser.role_name === 'gym' || currentUser.role_name === 'trainer') {
        const needsFirstLoginSetup = Number(currentUser.requires_first_login_setup) === 1;
        if (currentUser.role_name === 'gym' && currentUser.gym_billing_renewal_required) {
          navigate('/gym/contabilita', { replace: true });
        } else {
          navigate(needsFirstLoginSetup ? '/gym/first-login-setup' : '/gym', { replace: true });
        }
      } else if (currentUser.role_name === 'standalone') {
        navigate('/standalone-dashboard', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    }
  }, [currentUser, navigate, from]);

const handleSubmit = async (e) => {
  e.preventDefault();
  setIsLoading(true);
  setLoginError('');
  setLoginErrorCode('');
  try {
    const user = await login(username, password);
    if (user?.role_name === 'gym' && user?.gym_billing_renewal_required) {
      navigate('/gym/contabilita', { replace: true });
      setIsLoading(false);
      return;
    }
    
    // Skip version check in development
    if (process.env.NODE_ENV !== 'development') {
      try {
        const storedVersion = localStorage.getItem('app_version');
        const response = await fetch(
          `${resolveAssetPath('version.json')}?t=${Date.now()}`
        );
        
        if (response.ok) { // Verifica se la risposta è valida
          const data = await response.json();
          
          if (storedVersion && data.version !== storedVersion) {
            // Se la versione è diversa, aggiorna e ricarica
            localStorage.removeItem('app_version');
            window.location.reload(true);
          } else if (!storedVersion) {
            // Se non c'è versione salvata, salvala
            localStorage.setItem('app_version', data.version);
          }
        }
      } catch (versionError) {
        console.warn('Version check after login failed:', versionError);
      }
    }
  } catch (err) {
    // 🔒 Gestione specifica per accesso negato alla webapp
    if (err.message && err.message.includes('Accesso negato: questa piattaforma è riservata')) {
      setLoginError(err.message);
    } else if (err.message && err.message.includes('Account sospeso')) {
      setLoginError(err.message);
    } else if (err.loginCode) {
      setLoginErrorCode(err.loginCode);
      setLoginError(err.message);
    } else if (err.message && (err.message.includes('Utente non attivo') || err.message.includes('piano attivo') || err.message.includes('Contatta l\'assistenza') || err.message.includes('abbonamento'))) {
      setLoginError(err.message);
    } else if (err.message && (err.message.includes('Elite') || err.message.includes('riservato ai trainer'))) {
      // WEBAPP-031: Piano Elite - account gym non può usare webapp
      setLoginError(err.message);
    } else {
      setLoginError(err.message || t('login.invalidCredentials'));
    }
    setIsLoading(false);
  }
};

  const handleGymInfoChange = (e) => {
    const { name, value } = e.target;
    setGymInfo(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleGymInfoSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setRequestStatus({ message: '', type: '' });

    try {
      const response = await fetch('/api/gym_request.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(gymInfo)
      });
      
      const data = await response.json();
      
      if (data.success) {
        setRequestStatus({
          message: t('gymRequest.successMessage'),
          type: 'success'
        });
        
        setGymInfo(emptyGymRequestForm());
        
        // Torna al login dopo 3 secondi
        setTimeout(() => {
          setShowGymInfo(false);
          setRequestStatus({ message: '', type: '' });
        }, 3000);
      } else {
        const isDuplicate = response.status === 409;
        setRequestStatus({
          message: isDuplicate
            ? (data.message || t('gymRequest.duplicateError'))
            : (data.message || t('gymRequest.errorMessage')),
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Errore:', error);
      setRequestStatus({
        message: t('gymRequest.connectionError'),
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Language Selector per la pagina di login
  const LanguageSelector = ({ forDarkBackdrop = false }) => {
    const { i18n } = useTranslation();
    const currentLanguage = i18n.language;
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const [isDark, setIsDark] = useState(false);

    useEffect(() => {
      const checkDarkMode = () => {
        const isDarkMode = document.documentElement.classList.contains('dark');
        setIsDark(isDarkMode);
      };

      checkDarkMode();

      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.attributeName === 'class') {
            checkDarkMode();
          }
        });
      });

      observer.observe(document.documentElement, { attributes: true });

      return () => {
        observer.disconnect();
      };
    }, []);

    const onDark = forDarkBackdrop || isDark;

    const changeLanguage = (lng) => {
      i18n.changeLanguage(lng);
      setIsOpen(false);
      // Salva la preferenza nel localStorage
      localStorage.setItem('preferred_language', lng);
    };

    // Gestisce il click all'esterno per chiudere il dropdown
    useEffect(() => {
      const handleClickOutside = (event) => {
        if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
          setIsOpen(false);
        }
      };

      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, []);

    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`p-2 rounded-full transition-colors flex items-center ${
            onDark
              ? 'hover:bg-white/10 bg-white/5'
              : 'hover:bg-gray-200 bg-gray-100/60'
          }`}
          aria-label="Change language"
        >
          <Globe className={`h-5 w-5 ${
            onDark ? 'text-indigo-200' : 'text-indigo-600 group-hover:text-indigo-700'
          }`} />
          <span className={`ml-1 text-xs uppercase font-medium ${
            onDark ? 'text-gray-100' : 'text-gray-700'
          }`}>{currentLanguage}</span>
        </button>
        {isOpen && (
          <div className={`absolute right-0 mt-2 w-36 rounded-md shadow-lg py-1 z-50 border ${
            onDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          }`}>
            <button
              onClick={() => changeLanguage('it')}
              className={`block w-full text-left px-4 py-2 text-sm ${
                currentLanguage === 'it'
                  ? onDark
                    ? 'bg-indigo-900/30 text-indigo-300'
                    : 'bg-indigo-50 text-indigo-600'
                  : onDark
                    ? 'text-gray-300 hover:bg-gray-700'
                    : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              🇮🇹 Italiano
            </button>
            <button
              onClick={() => changeLanguage('en')}
              className={`block w-full text-left px-4 py-2 text-sm ${
                currentLanguage === 'en'
                  ? onDark
                    ? 'bg-indigo-900/30 text-indigo-300'
                    : 'bg-indigo-50 text-indigo-600'
                  : onDark
                    ? 'text-gray-300 hover:bg-gray-700'
                    : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              🇬🇧 English
            </button>
          </div>
        )}
      </div>
    );
  };

  /** Stesso gradiente della GymDashboard (area principale), non il solo gray-900 del SidebarLayout */
  const loginShellStyle = { background: isDark ? DASHBOARD_BG_DARK : DASHBOARD_BG_LIGHT };

  const loginCardLogo = (
    <div className="flex justify-center w-full mb-1">
      <GymLogo
        variant="full"
        className="!h-[4.75rem] sm:!h-[5.5rem] w-auto !max-w-[min(100%,340px)] shrink-0 text-[#372d83]"
      />
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col relative transition-colors" style={loginShellStyle}>
      <div className="absolute top-4 right-4 z-50">
        <LanguageSelector forDarkBackdrop={isDark} />
      </div>

      <div
        className={`flex flex-1 flex-col items-center justify-center px-4 py-8 sm:py-10 w-full mx-auto ${
          showGymInfo ? 'max-w-6xl' : 'max-w-md'
        }`}
      >
        <div
          className={`w-full rounded-2xl bg-white shadow-xl shadow-black/20 border border-gray-100 ${
            showGymInfo ? 'p-6 sm:p-8' : 'p-8 sm:p-10'
          }`}
        >
          {showGymInfo ? (
            <>
              <div className="mb-6 flex justify-center sm:justify-start border-b border-gray-100 pb-4">
                <GymLogo
                  variant="full"
                  className="!h-12 sm:!h-14 w-auto !max-w-[280px] shrink-0 text-[#372d83]"
                />
              </div>
              <GymAccessRequestForm
                gymInfo={gymInfo}
                onChange={handleGymInfoChange}
                onSubmit={handleGymInfoSubmit}
                onCancel={() => setShowGymInfo(false)}
                isLoading={isLoading}
                requestStatus={requestStatus}
              />
            </>
          ) : (
            <>
              {loginCardLogo}
              <p className="text-center text-sm text-gray-500 mb-8 -mt-1">{t('login.accessDashboard')}</p>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-4">
                  <div className="space-y-2">
                    <label htmlFor="username" className="block text-sm font-medium text-gray-600">
                      {t('login.username')}
                    </label>
                    <input
                      id="username"
                      type="text"
                      placeholder={t('login.usernamePlaceholder')}
                      className="px-4 py-3 w-full rounded-xl border border-gray-200 bg-gray-50 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-[#372d83]/40 focus:border-[#372d83] outline-none transition-shadow"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      required
                      autoComplete="username"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-600">
                      {t('login.password')}
                    </label>
                    <div className="relative">
                      <input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder={t('login.passwordPlaceholder')}
                        className="px-4 py-3 pr-12 w-full rounded-xl border border-gray-200 bg-gray-50 text-gray-900 placeholder:text-gray-400 focus:ring-2 focus:ring-[#372d83]/40 focus:border-[#372d83] outline-none transition-shadow"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        autoComplete="current-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded-md text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-[#372d83]/30"
                        aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end -mt-1">
                  <a
                    href="mailto:support@fitgymtrack.com?subject=Recupero%20password%20FitGymTrack"
                    className="text-sm text-gray-400 hover:text-[#372d83] transition-colors"
                  >
                    {t('login.forgotPassword')}
                  </a>
                </div>

                {loginError && (
                  <div className={`p-4 border rounded-xl ${
                    loginError.includes('Accesso negato: questa piattaforma è riservata')
                      ? 'bg-blue-50 border-blue-200'
                      : loginError.includes('Account sospeso')
                      ? 'bg-amber-50 border-amber-200'
                      : loginError.includes('Utente non attivo') || loginError.includes('piano attivo') || loginError.includes('Contatta l\'assistenza')
                      ? 'bg-amber-50 border-amber-200'
                      : loginError.includes('Elite') || loginError.includes('riservato ai trainer')
                      ? 'bg-blue-50 border-blue-200'
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-start">
                      <div className="flex-shrink-0">
                        {loginError.includes('Accesso negato: questa piattaforma è riservata') || loginError.includes('Elite') || loginError.includes('riservato ai trainer') ? (
                          <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                          </svg>
                        ) : loginError.includes('Account sospeso') || loginError.includes('Utente non attivo') || loginError.includes('piano attivo') || loginError.includes('Contatta l\'assistenza') ? (
                          <svg className="h-5 w-5 text-amber-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        ) : (
                          <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                      <div className="ml-3">
                        <p className={`text-sm ${
                          loginError.includes('Accesso negato: questa piattaforma è riservata') || loginError.includes('Elite') || loginError.includes('riservato ai trainer')
                            ? 'text-blue-800'
                            : loginError.includes('Account sospeso') || loginError.includes('Utente non attivo') || loginError.includes('piano attivo') || loginError.includes('Contatta l\'assistenza')
                            ? 'text-amber-800'
                            : 'text-red-700'
                        }`}>
                          {loginError}
                        </p>
                        {loginError.includes('Accesso negato: questa piattaforma è riservata') && (
                          <div className="mt-2">
                            <p className="text-xs text-blue-700">
                              💡 <strong>Suggerimento:</strong> Scarica l'app mobile FitGymTrack dal tuo store per accedere alle funzionalità della palestra.
                            </p>
                          </div>
                        )}
                        {loginErrorCode === 'GYM_BILLING_EXPIRED' && (
                          <div className="mt-2">
                            <p className="text-xs text-amber-700">
                              {t('login.billingExpiredHint')}
                            </p>
                            <a
                              href="mailto:support@fitgymtrack.com?subject=Rinnovo%20abbonamento%20palestra"
                              className="mt-2 inline-block text-xs font-medium text-amber-800 underline hover:no-underline"
                            >
                              {t('login.contactSupport')}
                            </a>
                          </div>
                        )}
                        {loginErrorCode === 'GYM_PLAN_INACTIVE' && (
                          <div className="mt-2">
                            <p className="text-xs text-amber-700">
                              {t('login.planInactiveHint')}
                            </p>
                          </div>
                        )}
                        {!loginErrorCode && (loginError.includes('Utente non attivo') || loginError.includes('piano attivo') || loginError.includes('Contatta l\'assistenza')) && (
                          <div className="mt-2">
                            <p className="text-xs text-amber-700">
                              {t('login.planInactiveHint')}
                            </p>
                          </div>
                        )}
                        {loginError.includes('Elite') && loginError.includes('trainer') && (
                          <div className="mt-2">
                            <p className="text-xs text-blue-700">
                              💡 Con piano Elite l'accesso alla piattaforma web è riservato ai trainer. Utilizza l'app mobile per la gestione.
                            </p>
                          </div>
                        )}
                        {loginError.includes('Account sospeso') && (
                          <div className="mt-2">
                            <p className="text-xs text-amber-700">
                              💡 <strong>Nota:</strong> Contatta la palestra per riattivare il tuo account.
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  className="w-full flex justify-center items-center rounded-xl py-3.5 px-4 font-semibold text-white bg-gradient-to-r from-[#372d83] to-[#5c4bb0] hover:from-[#2d246c] hover:to-[#4a3d96] shadow-md shadow-[#372d83]/25 transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#372d83] disabled:opacity-70 disabled:cursor-not-allowed"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <span className="flex items-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {t('login.loggingIn')}
                    </span>
                  ) : (
                    <span className="flex items-center">
                      {t('login.loginButton')}
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </span>
                  )}
                </button>
              </form>

              <div className="mt-8 pt-6 border-t border-gray-100">
                <p className="text-xs text-center text-gray-400 mb-3">{t('login.registrationOptions')}</p>
                <div className="flex flex-col gap-2 text-sm">
                  <Link
                    to="/standalone-register"
                    className="flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-2.5 text-gray-700 hover:border-[#372d83]/25 hover:bg-white transition"
                  >
                    <span className="font-medium">{t('login.personalAccount')}</span>
                    <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                  </Link>
                  <button
                    type="button"
                    onClick={() => setShowGymInfo(true)}
                    className="w-full flex items-center justify-between rounded-lg border border-gray-100 bg-gray-50/80 px-3 py-2.5 text-left text-gray-700 hover:border-emerald-500/25 hover:bg-white transition"
                  >
                    <span className="font-medium">{t('login.gymOwner')}</span>
                    <ChevronRight className="h-4 w-4 text-gray-400 shrink-0" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
        
        <p
          className={`mt-8 text-center text-xs ${
            isDark ? 'text-gray-400' : 'text-gray-600'
          }`}
        >
          © {t('common.appName')} —{' '}
          <a
            href="https://www.iubenda.com/privacy-policy/65746024"
            className={`iubenda-black iubenda-noiframe underline-offset-2 hover:underline transition ${
              isDark ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-800'
            }`}
            title="Privacy Policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
          <span className={`mx-2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>·</span>
          <a
            href="https://www.iubenda.com/privacy-policy/65746024/cookie-policy"
            className={`iubenda-black iubenda-noiframe underline-offset-2 hover:underline transition ${
              isDark ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-800'
            }`}
            title="Cookie Policy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Cookie Policy
          </a>
        </p>
      </div>
    </div>
  );
};

export default LoginPage;